/**
 * One file (Files screen 3): the file itself on the left, and on the right
 * its metadata and details.
 */
import { Download, Loader2, MessageSquare, Trash2 } from "lucide-react";
import React, {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FileFacts from "../../components/Files/FileFacts";
import FilePreview from "../../components/Files/FilePreview";
import { useProject } from "../../context/ProjectContext";
import { SpreadsheetViewerProvider } from "../../context/SpreadsheetViewerContext";
import { useToast } from "../../context/ToastContext";
import { useDealRoomActions } from "../../hooks/useDealRoomActions";
import { useFileLibrary } from "../../hooks/useFileLibrary";
import { isFileBeingRead } from "../../components/AddFiles/DealFileStatus";
import { saveFileToDisk } from "../../services/fileApi";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";
import { citationFromLocation } from "../../utils/dopplerUri";
import { FileType } from "../../types/fileType";
import { formatFileSize } from "../../utils/formatUtils";
import BackButton from "../../components/BackButton";
import { canWriteFolder } from "../../utils/folderSharing";

// Lazy, like the workflow pages' rails: the chat page is a large module.
const FileChatRail = lazy(() => import("../../components/Files/FileChatRail"));

const FileDetail: React.FC = () => {
	const { fileId } = useParams<{ fileId: string }>();
	const { search } = useLocation();
	const navigate = useNavigate();
	// Set when a citation opened this page in its own tab (see openCitationTab).
	const citation = useMemo(
		() => citationFromLocation(Number(fileId), search),
		[fileId, search],
	);
	const library = useFileLibrary();
	const { setSelectedProject } = useProject();
	const { showToast } = useToast();
	const actions = useDealRoomActions({
		onFileDeleted: ({ dealId }) => navigate(`/files/deals/${dealId}`),
	});

	const file = library.findFile(Number(fileId));
	const deal = file ? library.findDeal(file.dealId) : undefined;
	// The deal page's rule: a view-only teammate opens and downloads, never
	// deletes.
	const canDelete = deal ? canWriteFolder(deal.node.folder) : false;

	const handleDownload = useCallback(async () => {
		if (!file) return;
		try {
			await saveFileToDisk(file.id);
		} catch (error) {
			showToast(friendlyErrorMessage(error, "Couldn't download this file."));
		}
	}, [file, showToast]);

	// Chat opens beside the file, as on the workflow pages. While it is open
	// it takes the metadata panel's place: two panels beside a document leave
	// too little room for the document.
	const [chatOpen, setChatOpen] = useState(false);
	const projectId = deal?.node.folder.project_id ?? null;
	const projectName = deal?.name ?? "";
	// The chat is about this deal's room, as when it is picked in Chat.
	useEffect(() => {
		if (chatOpen && projectId) {
			setSelectedProject({ id: projectId, name: projectName });
		}
	}, [chatOpen, projectId, projectName, setSelectedProject]);

	if (library.loading) {
		return (
			<div className="flex h-full items-center justify-center gap-2 bg-white text-sm text-text-muted">
				<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
				Loading file…
			</div>
		);
	}
	if (!file && citation && !library.error) {
		return <CitedFileOnly fileId={Number(fileId)} citation={citation} />;
	}
	if (!file) {
		return (
			<div className="flex h-full flex-col items-start gap-3 bg-white px-8 py-10">
				<p className="text-sm text-gray-900">
					{library.error ?? "This file isn't in your files."}
				</p>
				<Link to="/files" className="text-sm font-semibold text-[#004C4B]">
					Back to Deal Room
				</Link>
			</div>
		);
	}

	const pages = file.document?.page_count;

	return (
		<SpreadsheetViewerProvider>
			<div className="flex h-full flex-col bg-white">
				<header className="flex h-[60px] flex-shrink-0 items-center gap-3.5 border-b border-[#eef0f2] px-6">
					<BackButton
						to={`/files/deals/${file.dealId}`}
						label={`Back to ${file.dealName}`}
					/>
					<div className="flex min-w-0 flex-col">
						<h1 className="truncate text-[15px] font-semibold text-gray-900">
							{file.filename}
						</h1>
						<span className="text-xs text-text-muted">
							{pages ? `${pages} pages · ` : ""}
							{formatFileSize(file.file_size)}
						</span>
					</div>
					<button
						type="button"
						onClick={handleDownload}
						className="ml-auto inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-800 hover:bg-gray-50"
					>
						<Download className="h-4 w-4" aria-hidden />
						Download
					</button>
					{/* Same rule as a deal row: no delete while Doppler is still
					    reading the file, or while an upload into the deal runs. */}
					{canDelete && !isFileBeingRead(file) && (
						<button
							type="button"
							disabled={actions.isUploadingInto(file.dealId)}
							title={
								actions.isUploadingInto(file.dealId)
									? "Files are still being added to this deal. You can delete once that finishes."
									: "Delete file"
							}
							onClick={() =>
								actions.requestDeleteFile({
									file,
									dealId: file.dealId,
									dealName: file.dealName,
								})
							}
							className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:border-gray-200 disabled:hover:bg-white"
						>
							<Trash2 className="h-4 w-4" aria-hidden />
							Delete
						</button>
					)}
					{projectId && (
						<button
							type="button"
							onClick={() => setChatOpen((open) => !open)}
							aria-pressed={chatOpen}
							className={`inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 text-[13px] font-medium ${
								chatOpen
									? "border-[#008782] bg-[#E6F2F1] text-[#004C4B]"
									: "border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
							}`}
						>
							<MessageSquare className="h-4 w-4" aria-hidden />
							Chat
						</button>
					)}
				</header>
				<div className="flex min-h-0 flex-1 flex-col lg:flex-row">
					<div className="min-h-[400px] min-w-0 flex-1 bg-[#f4f5f6]">
						<FilePreview
							fileId={file.id}
							filename={file.filename}
							mimeType={file.mime_type}
							onDownload={handleDownload}
							citation={citation}
						/>
					</div>
					{chatOpen && projectId ? (
						<Suspense
							fallback={
								<div className="flex w-[440px] items-center justify-center border-l border-gray-200 bg-white">
									<Loader2 className="h-4 w-4 animate-spin text-text-muted" aria-hidden />
								</div>
							}
						>
							<FileChatRail
								fileId={file.id}
								filename={file.filename}
								projectId={projectId}
								onClose={() => setChatOpen(false)}
							/>
						</Suspense>
					) : citation ? null : (
						// Opened from a citation, the document is the whole point: the
						// metadata panel only takes width from the cited page or sheet.
						<FileFacts file={file} />
					)}
				</div>
				{actions.element}
			</div>
		</SpreadsheetViewerProvider>
	);
};

/**
 * A cited file that is not in the reader's Deal Room list (a connector file, a
 * deal they were not shown): the backend still decides whether they may read
 * it, so show the preview alone rather than a dead end.
 */
const CitedFileOnly: React.FC<{
	fileId: number;
	citation: NonNullable<ReturnType<typeof citationFromLocation>>;
}> = ({ fileId, citation }) => {
	const { showToast } = useToast();
	const isPdf = citation.kind === FileType.PDF;
	const name = citation.filename ?? (isPdf ? "Cited document" : "Cited workbook");
	// The preview picks its viewer from the name and type; a cited workbook's
	// name may not carry an extension.
	const previewName = isPdf || /\.(xlsx|xlsm|xls|csv)$/i.test(name) ? name : `${name}.xlsx`;
	const handleDownload = useCallback(async () => {
		try {
			await saveFileToDisk(fileId);
		} catch (error) {
			showToast(friendlyErrorMessage(error, "Couldn't download this file."));
		}
	}, [fileId, showToast]);
	return (
		<SpreadsheetViewerProvider>
			<div className="flex h-full flex-col bg-white">
				<header className="flex h-[60px] flex-shrink-0 items-center gap-3.5 border-b border-[#eef0f2] px-6">
					<BackButton to="/files" label="Back to Deal Room" />
					<h1 className="truncate text-[15px] font-semibold text-gray-900">{name}</h1>
				</header>
				<div className="min-h-0 flex-1 bg-[#f4f5f6]">
					<FilePreview
						fileId={fileId}
						filename={previewName}
						mimeType={isPdf ? "application/pdf" : ""}
						onDownload={handleDownload}
						citation={citation}
					/>
				</div>
			</div>
		</SpreadsheetViewerProvider>
	);
};

export default FileDetail;
